// JWT claims que el stack Supabase emite + claims custom que un hook nuestro
// inyecta. El middleware Express los valida en cada request y los expone como
// `req.ctx` (ver RequestContext más abajo).
//
// El hook de Supabase Auth (a implementar en F0-3) lee `tenant_users` y resuelve:
//   - `tenant_id_activo`: el tenant en el que el usuario está operando ahora
//   - `rol`: rol del usuario en ese tenant
//   - `tenants_disponibles`: lista para la pantalla L0 ("elegir tenant")
//
// Si el usuario tiene solo 1 tenant, L0 se salta y `tenant_id_activo` se setea
// automáticamente. Si tiene >1 (caso típico: Super Admin de Jidoka), L0 lista
// los tenants y deja al usuario elegir antes de continuar.
import { z } from 'zod';
import { roleSchema } from './role.js';
/** Entrada en `tenants_disponibles` del JWT — info mínima para que L0 liste opciones. */
export const tenantAccessibleSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string().trim().min(1).max(120),
    rol: roleSchema,
});
/**
 * Claims completos del JWT después de pasar por el hook custom.
 * Los campos `sub`, `email`, `aud`, `exp`, `iat` son estándar de Supabase Auth.
 * Los demás los inyecta nuestro hook.
 */
export const jwtClaimsSchema = z.object({
    // Claims estándar de Supabase Auth
    sub: z.string().uuid(),
    email: z.string().email(),
    aud: z.string(),
    exp: z.number().int().positive(),
    iat: z.number().int().positive(),
    // Claims custom inyectados por hook (apps/api confía en estos vía firma JWT)
    tenant_id_activo: z.string().uuid(),
    rol: roleSchema,
    tenants_disponibles: z.array(tenantAccessibleSchema).min(1),
});
/**
 * Contexto que el middleware deja en `req.ctx` después de verificar el JWT.
 * Es lo que cualquier handler debe usar — JAMÁS leer `req.body.tenant_id`.
 *
 * Si la request viene de un super_admin suplantando a otro usuario:
 *   - `user_id` = el usuario SUPLANTADO (cuyo contexto se está usando)
 *   - `rol` = el rol del usuario suplantado
 *   - `via_suplantacion` = true
 *   - `super_admin_actor_id` = el id real del super_admin que ejecuta
 *
 * Para auditoría: registrar siempre ambos (ver `eventos_auditoria` columnas
 * `user_id_actor` + `super_admin_actor_id` + `ejecutada_via_suplantacion`).
 */
export const requestContextSchema = z.object({
    user_id: z.string().uuid(),
    email: z.string().email(),
    tenant_id: z.string().uuid(),
    rol: roleSchema,
    via_suplantacion: z.boolean().default(false),
    super_admin_actor_id: z.string().uuid().nullable().default(null),
});
/**
 * Helper: deriva el `RequestContext` desde los claims JWT verificados.
 * Centralizado acá para que apps/api y tests adversariales usen exactamente la
 * misma lógica.
 */
export function contextFromClaims(claims, options) {
    return requestContextSchema.parse({
        user_id: claims.sub,
        email: claims.email,
        tenant_id: claims.tenant_id_activo,
        rol: claims.rol,
        via_suplantacion: options?.via_suplantacion ?? false,
        super_admin_actor_id: options?.super_admin_actor_id ?? null,
    });
}
//# sourceMappingURL=session.js.map